import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Spin,
  Table,
  Input,
  Menu,
  Dropdown,
  Tag,
  Switch,
} from "antd";
import { useSelector } from "react-redux";
import { RiAddLine, RiFileList2Line, RiBookmark3Line } from "react-icons/ri";
import {
  getAllAiToolsPaginatedSearch,
  deleteAiToolById,
  updateAiToolApprovalById,
} from "@api/seo/aiTool";

const { Search } = Input;

const AiToolsList = ({ history }) => {
  const { user } = useSelector((state) => state.auth);
  const [aiTools, setAiTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";

    setSearchText(search);
    setPagination({ current: page, pageSize, total: 0 });
    fetchAiTools(page, pageSize, search);
  }, []);

  const fetchAiTools = async (page, pageSize, searchText = "") => {
    setLoading(true);
    try {
      const res = await getAllAiToolsPaginatedSearch(
        page,
        pageSize,
        searchText
      );
      setAiTools(res.data.aiTools);
      setPagination((p) => ({
        ...p,
        total: res.data.paginationData.totalAiTools,
        current: res.data.paginationData.page,
        pageSize: res.data.paginationData.perPage,
      }));
    } catch (err) {
      console.error("Fetch aiTools error", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchText) searchParams.set("search", searchText);

    history.push({ search: searchParams.toString() });
    setPagination({ ...pagination, current: page, pageSize });
    fetchAiTools(page, pageSize, searchText);
  };

  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination({ ...pagination, current: 1 }); // Reset to first page
    fetchAiTools(1, pagination.pageSize, searchValue);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  // Function to handle ai-tool update
  const handleAiToolUpdate = (id) => {
    const basePath =
      user?.role === "superadmin" ? "/super-admin" : "/marketing-admin";
    history.push(`${basePath}/seo/ai-tool/${id}/update`);
  };

  // Function to handle ai-tool delete
  const handleAiToolDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this AI tool?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteAiToolById(id);
          fetchAiTools(pagination.current, pagination.pageSize);
        } catch (error) {
          console.error("Error deleting AI tool", error);
        }
      },
    });
  };

  // Function to handle ai-tool approval
  const handleAiToolApproval = async (checked, id) => {
    try {
      await updateAiToolApprovalById(id, { approved: checked });
      fetchAiTools(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error updating AI tool approval", error);
    }
  };

  // Function to format date
  const formatDate = (date) => {
    if (!date) return "";
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Date(date).toLocaleDateString("en-US", options);
  };

  const menu = (
    <Menu>
      <Menu.Item
        key="1"
        icon={<RiFileList2Line />}
        onClick={() => {
          const basePath =
            user?.role === "superadmin" ? "/super-admin" : "/marketing-admin";
          history.push(`${basePath}/seo/ai-tool/create`);
        }}
      >
        Create AI Tool
      </Menu.Item>
      <Menu.Item
        key="2"
        icon={<RiBookmark3Line />}
        onClick={() => {
          const basePath =
            user?.role === "superadmin" ? "/super-admin" : "/marketing-admin";
          history.push(`${basePath}/seo/ai-tools/ai-tool-categories`);
        }}
      >
        AI Tool Categories
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) =>
        name.length > 25 ? `${name.substring(0, 25)}...` : name,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => <Tag color="blue">{category?.name}</Tag>,
    },
    {
      title: "Inputs",
      dataIndex: "inputs",
      key: "inputs",
      render: (inputs) => inputs.length,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) => formatDate(date),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            type="primary"
            size="small"
            style={{ marginRight: "15px" }}
            onClick={() => handleAiToolUpdate(record._id)}
          >
            Edit
          </Button>
          <Button
            type="danger"
            size="small"
            onClick={() => handleAiToolDelete(record._id)}
          >
            Delete
          </Button>
        </>
      ),
    },
    {
      title: "Approved",
      dataIndex: "approved",
      key: "approved",
      render: (approved, record) => (
        <Switch
          checked={approved}
          onChange={(checked) => handleAiToolApproval(checked, record._id)}
        />
      ),
    },
  ];

  return (
    <Card style={{ borderRadius: "0", height: "100vh", overflowY: "auto" }}>
      <Row justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <h3>AI Tools List</h3>
        </Col>
        <Col>
          <Button
            onClick={() => {
              const basePath =
                user?.role === "superadmin"
                  ? "/super-admin"
                  : "/marketing-admin";
              history.push(`${basePath}/seo/ai-apps`);
            }}
            style={{ marginRight: 10 }}
          >
            Open AI Apps
          </Button>
          <Button
            onClick={() => {
              const basePath =
                user?.role === "superadmin"
                  ? "/super-admin"
                  : "/marketing-admin";
              history.push(`${basePath}/seo/ai-glossary`);
            }}
            style={{ marginRight: 10 }}
          >
            Open AI Glossary
          </Button>
          <Search
            placeholder="Search by name/description"
            style={{ width: "300px", marginRight: 10 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
          />
          <Dropdown overlay={menu} placement="bottomRight">
            <Button icon={<RiAddLine />} style={{ marginLeft: "10px" }} />
          </Dropdown>
        </Col>
      </Row>
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={aiTools}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
          }}
          loading={loading}
          onChange={({ current, pageSize }) =>
            handlePaginationChange(current, pageSize)
          }
          rowKey={(record) => record._id}
          scroll={{ y: "calc(100vh - 300px)" }}
        />
      </Spin>
    </Card>
  );
};

export default AiToolsList;
